import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { getExhibitionSettings, selectIsLogoutProcessing } from '@app/app.reducer';
import { FieldKey, OptionKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { UploadedFiles } from '@core/data-access/models/file-upload.model';
import { FormFactory } from '@core/data-access/models/form/form-mediator.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { PersonType } from '@core/data-access/models/person.model';
import { SelectedTicketSendingOptions } from '@core/data-access/models/shared/ticket-sending-options.model';
import { DynamicFieldOptionsTranslationFactory } from '@core/data-access/services/dynamic-form-field/dynamic-field-options-translation-factory.service';
import { DynamicFormFactory } from '@core/data-access/services/dynamic-form-field/dynamic-form-factory.service';
import { selectIsReadOnlyBuyerFormFields } from '@core/data-access/store/helper.selectors';
import {
  selectSelectedTicketSendingOptionByTariffUuid,
  selectSelectedTicketSendingOptions
} from '@core/data-access/store/shared/shared.selectors';
import { selectBuyerPerson } from '@core/features/buyer-information/store/buyer-information.selectors';
import { TicketHolderForms } from '@core/features/ticket-assignment/models/ticket-assignment-store.model';
import {
  TicketHolderFormStates,
  TicketHolderFormViewModel
} from '@core/features/ticket-assignment/models/ticket-holder-form.model';
import { TicketHolderDynamicFormFieldsFactory } from '@core/features/ticket-assignment/services/ticket-holder-dynamic-form-fields-factory.service';
import {
  selectTicketHolderFormFieldsByTariffUuid,
  selectTicketHolderForms,
  selectTicketHolderFormsTariffUuidsByPersonUuid
} from '@core/features/ticket-assignment/store/ticket-assignment.selectors';
import {
  reduceTicketHolderFormStatePipe,
  updateFormFieldsFilterPipe
} from '@core/features/ticket-assignment/utils/ticket-holder-forms-factory-helpers';
import { select, Store } from '@ngrx/store';
import { TariffUuid, TariffUuids } from '@products/models/tariff.model';
import {
  getBookedTariffByTariffUuid,
  selectTicketPersonIdByBookedTariffUuid
} from '@store/products/booking/tariff/booking-tariff.selectors';
import { getSendingOptions } from '@store/products/product-selection/product-selection.selectors';
import { getIsTranslationLoaded } from '@store/translation/translation.selectors';
import {
  filter as _filter,
  reduce as _reduce,
  difference,
  includes,
  intersection,
  isEmpty,
  isEqual,
  isNil,
  keys,
  pickBy,
  reduce
} from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  concatMap,
  filter,
  first,
  from,
  iif,
  map,
  merge,
  Observable,
  of,
  OperatorFunction,
  pairwise,
  pipe,
  startWith,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TicketHolderFormsFactory implements FormFactory {
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(Store);
  readonly #dynamicFormFactory = inject(DynamicFormFactory);
  readonly #dynamicFormFieldsFactory = inject(TicketHolderDynamicFormFieldsFactory);
  readonly #dynamicFieldOptionsTranslationFactory = inject(DynamicFieldOptionsTranslationFactory);

  readonly #initialState = new Map();

  readonly #resetFormState$ = new BehaviorSubject(false);

  readonly #ticketHolderForms$ = this.#store.select(selectTicketHolderForms);
  readonly #selectedTicketSendingOptions$ = this.#store.select(selectSelectedTicketSendingOptions);

  readonly #updateFormStatePairwiseFilter$ = this.updateFormStatePairwiseFilter$();

  readonly #isFormStateCreated = signal(false);

  readonly state = toSignal(this.state$(), { initialValue: this.#initialState });

  resetFormState() {
    this.#resetFormState$.next(true);
  }

  private state$(): Observable<TicketHolderFormStates> {
    return merge(this.setFormState$(), this.updateFormState$(), this.resetFormsState$()).pipe(
      takeUntilDestroyed(this.#destroyRef)
    );
  }

  private setFormState$(): Observable<TicketHolderFormStates> {
    return this.#ticketHolderForms$.pipe(
      startWith({}),
      pairwise(),
      filter(
        ([previousTicketHolderForms, currentTicketHolderForms]) =>
          !isEqual(keys(previousTicketHolderForms).length, keys(currentTicketHolderForms).length)
      ),
      map(([previousTicketHolderForms, currentTicketHolderForms]) => {
        const previousTariffUuids = keys(previousTicketHolderForms);
        const currentTariffUuids = keys(currentTicketHolderForms);

        const createOrAddFormToFormState = previousTariffUuids.length < currentTariffUuids.length;
        const tariffUuidDifference = createOrAddFormToFormState
          ? difference(currentTariffUuids, previousTariffUuids)
          : difference(previousTariffUuids, currentTariffUuids);

        return {
          ticketHolderForms: currentTicketHolderForms,
          createOrAddFormToFormState,
          tariffUuidDifference
        };
      }),
      switchMap(({ createOrAddFormToFormState, ticketHolderForms, tariffUuidDifference }) =>
        iif(
          () => createOrAddFormToFormState,
          this.createOrAddFormToFormState$(ticketHolderForms, tariffUuidDifference),
          this.removeFormFromFormStates$(tariffUuidDifference)
        )
      )
    );
  }

  private createOrAddFormToFormState$(
    ticketHolderForms: TicketHolderForms,
    createOrAddFormTariffUuids: TariffUuids
  ): Observable<TicketHolderFormStates> {
    return iif(
      () => !this.#isFormStateCreated(),
      this.createFormStates$(ticketHolderForms, createOrAddFormTariffUuids),
      this.addFormStateToFormStates$(createOrAddFormTariffUuids)
    );
  }

  private createFormStates$(
    ticketHolderForms: TicketHolderForms,
    createFormTariffUuids: TariffUuids
  ): Observable<TicketHolderFormStates> {
    return from(createFormTariffUuids).pipe(
      this.createFormSatePipe(ticketHolderForms),
      tap(() => this.#isFormStateCreated.set(true))
    );
  }

  private addFormStateToFormStates$(addFormTariffUuids: TariffUuids): Observable<TicketHolderFormStates> {
    return from(addFormTariffUuids).pipe(
      this.createFormSatePipe({}),
      map((updatedFormStates) => new Map([...this.state(), ...updatedFormStates]))
    );
  }

  private createFormSatePipe(ticketHolderForms: TicketHolderForms) {
    return pipe(
      concatMap((tariffUuid: TariffUuid) =>
        combineLatest({
          bookedTariff: this.#store.select(getBookedTariffByTariffUuid(tariffUuid)),
          selectedTicketSendingOption: this.#store.select(selectSelectedTicketSendingOptionByTariffUuid(tariffUuid))
        }).pipe(
          first(({ bookedTariff }) => !isNil(bookedTariff)),
          map(({ bookedTariff, selectedTicketSendingOption }) => ({
            bookedTariff: bookedTariff!,
            selectedTicketSendingOption,
            person: ticketHolderForms[tariffUuid]?.person || null,
            formFields: ticketHolderForms[tariffUuid]?.formFields || {}
          })),
          withLatestFrom(this.#store.select(selectIsReadOnlyBuyerFormFields)),
          switchMap(([{ bookedTariff, selectedTicketSendingOption, person, formFields }, isReadOnlyBuyerFormFields]) =>
            this.#dynamicFormFieldsFactory
              .createDynamicTicketHolderFormFields$(
                bookedTariff,
                selectedTicketSendingOption,
                formFields,
                person,
                isReadOnlyBuyerFormFields
              )
              .pipe(
                tap((dynamicFormFields) =>
                  this.#dynamicFieldOptionsTranslationFactory.createTranslatedDynamicFieldOptions(
                    FormName.TicketHolder,
                    dynamicFormFields
                  )
                ),
                switchMap((dynamicFormFields) =>
                  this.#store.pipe(
                    select(getIsTranslationLoaded),
                    first((isTranslationLoaded) => isTranslationLoaded),
                    map(() => {
                      const initialViewModel: TicketHolderFormViewModel = {
                        isReadOnlyBuyerFormFields,
                        checkboxUncheckGuards: {}
                      };

                      const formGroup = this.#dynamicFormFactory.createForm(dynamicFormFields, formFields);

                      return {
                        viewModel: this.setViewModel(initialViewModel, formGroup),
                        dynamicFormFields,
                        form: {
                          formName: FormName.TicketHolder,
                          formGroup
                        },
                        person,
                        tariffUuid
                      };
                    })
                  )
                )
              )
          )
        )
      ),
      reduceTicketHolderFormStatePipe()
    );
  }

  private removeFormFromFormStates$(removeFormTariffUuids: TariffUuids): Observable<TicketHolderFormStates> {
    return of(removeFormTariffUuids).pipe(
      filter(() => this.#isFormStateCreated()),
      map(
        (removeFormTariffUuids) => new Map(_filter([...this.state()], ([key]) => !includes(removeFormTariffUuids, key)))
      )
    );
  }

  private updateFormState$(): Observable<TicketHolderFormStates> {
    return toObservable(this.#isFormStateCreated).pipe(
      filter((isFormStateCreated) => isFormStateCreated),
      switchMap(() =>
        merge(
          this.recreateFormOnTicketHolderChange$(),
          this.updateFormStateOnSetTicketHolderForms$(),
          this.updateFormStateOnSendingOptionChange$(),
          this.updateFormStateOnSendToOwnerChange$(),
          this.updateFormStateOnGDPRChange$(),
          this.updateFormStateOnBuyerPersonChange$()
        )
      )
    );
  }

  private recreateFormOnTicketHolderChange$(): Observable<TicketHolderFormStates> {
    return this.#updateFormStatePairwiseFilter$.pipe(
      map(([previousTicketHolderFormChanges, currentTicketHolderFormChanges]) =>
        _reduce(
          keys(currentTicketHolderFormChanges),
          (ticketHolderForms: TicketHolderForms, tariffUuid) => {
            const previousPersonChange = previousTicketHolderFormChanges[tariffUuid]?.person;
            const currentPersonChange = currentTicketHolderFormChanges[tariffUuid]?.person;

            if (isEqual(previousPersonChange, currentPersonChange)) {
              return ticketHolderForms;
            }

            ticketHolderForms[tariffUuid] = currentTicketHolderFormChanges[tariffUuid];

            return ticketHolderForms;
          },
          {}
        )
      ),
      filter((ticketHolderForms) => !isEmpty(ticketHolderForms)),
      switchMap((ticketHolderForms) => from(keys(ticketHolderForms)).pipe(this.createFormSatePipe(ticketHolderForms))),
      map((updatedFormStates) => new Map([...this.state(), ...updatedFormStates]))
    );
  }

  private updateFormStateOnSetTicketHolderForms$(): Observable<TicketHolderFormStates> {
    return this.#updateFormStatePairwiseFilter$.pipe(
      switchMap(([previousTicketHolderFormChanges, currentTicketHolderFormChanges]) =>
        from(keys(currentTicketHolderFormChanges)).pipe(
          updateFormFieldsFilterPipe(
            previousTicketHolderFormChanges,
            currentTicketHolderFormChanges,
            ({ changedFormFields }) =>
              !isEmpty(changedFormFields) && isEmpty(intersection(keys(changedFormFields), [FieldKey.SendToOwner]))
          ),
          map(({ tariffUuid, changedFormFields }) => {
            const { viewModel, dynamicFormFields, form, person } = this.state().get(tariffUuid);
            const { formGroup } = form;

            const updatedDynamicFormFields = this.#dynamicFormFactory.updateForm(
              dynamicFormFields,
              changedFormFields,
              formGroup
            );

            return {
              viewModel: this.setViewModel(viewModel, formGroup),
              dynamicFormFields: updatedDynamicFormFields,
              form,
              person,
              tariffUuid
            };
          }),
          reduceTicketHolderFormStatePipe()
        )
      ),
      map((updatedFormStates) => new Map([...this.state(), ...updatedFormStates]))
    );
  }

  private updateFormStateOnSendingOptionChange$(): Observable<TicketHolderFormStates> {
    return this.#selectedTicketSendingOptions$.pipe(
      this.isFormsStateSetPipe<SelectedTicketSendingOptions>(),
      pairwise(),
      filter(([previousTicketSendingOptionChanges, currentTicketSendingOptionChanges]) => {
        const previousTicketSendingOptionChangesKeys = keys(previousTicketSendingOptionChanges);
        const currentTicketSendingOptionChangesKeys = keys(currentTicketSendingOptionChanges);

        return (
          !isEmpty(previousTicketSendingOptionChangesKeys) &&
          !isEmpty(currentTicketSendingOptionChangesKeys) &&
          isEqual(previousTicketSendingOptionChangesKeys.length, currentTicketSendingOptionChangesKeys.length)
        );
      }),
      map(([previousTicketSendingOptionChanges, currentTicketSendingOptionChanges]) =>
        keys(
          pickBy(
            previousTicketSendingOptionChanges,
            (selectedTicketSendingOption, tariffUuid) =>
              !isEqual(selectedTicketSendingOption, currentTicketSendingOptionChanges[tariffUuid])
          )
        )
      ),
      switchMap((changedTariffUuids) =>
        from(changedTariffUuids).pipe(
          concatMap((tariffUuid) =>
            combineLatest({
              ticketPersonId: this.#store.select(selectTicketPersonIdByBookedTariffUuid(tariffUuid)),
              formFields: this.#store.select(selectTicketHolderFormFieldsByTariffUuid(tariffUuid)),
              exhibitionSettings: this.#store.select(getExhibitionSettings),
              ticketSendingOptions: this.#store.select(getSendingOptions),
              selectedTicketSendingOption: this.#store.select(selectSelectedTicketSendingOptionByTariffUuid(tariffUuid))
            }).pipe(
              first(
                ({ ticketPersonId, exhibitionSettings, ticketSendingOptions, selectedTicketSendingOption }) =>
                  !isNil(ticketPersonId) &&
                  !isEmpty(exhibitionSettings) &&
                  !isEmpty(ticketSendingOptions) &&
                  !isNil(selectedTicketSendingOption)
              ),
              map(
                ({
                  ticketPersonId,
                  formFields,
                  exhibitionSettings,
                  ticketSendingOptions,
                  selectedTicketSendingOption
                }) => {
                  const { viewModel, dynamicFormFields, form, person } = this.state().get(tariffUuid);

                  return {
                    viewModel,
                    dynamicFormFields: this.#dynamicFormFactory.updateForm(
                      this.#dynamicFormFieldsFactory.updateFormOnTicketSendingOptionChange(
                        dynamicFormFields,
                        form.formGroup,
                        person,
                        formFields,
                        ticketPersonId,
                        exhibitionSettings,
                        ticketSendingOptions,
                        selectedTicketSendingOption
                      ),
                      formFields,
                      form.formGroup
                    ),
                    form,
                    person,
                    tariffUuid
                  };
                }
              )
            )
          ),
          reduceTicketHolderFormStatePipe()
        )
      ),
      map((updatedFormStates) => new Map([...this.state(), ...updatedFormStates]))
    );
  }

  private updateFormStateOnSendToOwnerChange$(): Observable<TicketHolderFormStates> {
    return this.#updateFormStatePairwiseFilter$.pipe(
      switchMap(([previousTicketHolderFormChanges, currentTicketHolderFormChanges]) =>
        from(keys(currentTicketHolderFormChanges)).pipe(
          updateFormFieldsFilterPipe(
            previousTicketHolderFormChanges,
            currentTicketHolderFormChanges,
            ({ changedFormFields }) =>
              !isEmpty(changedFormFields) && !isEmpty(intersection(keys(changedFormFields), [FieldKey.SendToOwner]))
          ),
          concatMap(({ tariffUuid }) =>
            combineLatest({
              formFields: this.#store.select(selectTicketHolderFormFieldsByTariffUuid(tariffUuid)),
              ticketPersonId: this.#store.select(selectTicketPersonIdByBookedTariffUuid(tariffUuid)),
              selectedTicketSendingOption: this.#store.select(
                selectSelectedTicketSendingOptionByTariffUuid(tariffUuid)
              ),
              exhibitionSettings: this.#store.select(getExhibitionSettings)
            }).pipe(
              first(
                ({ ticketPersonId, selectedTicketSendingOption, exhibitionSettings }) =>
                  !isNil(ticketPersonId) && !isNil(selectedTicketSendingOption) && !isEmpty(exhibitionSettings)
              ),
              map(({ formFields, ticketPersonId, exhibitionSettings, selectedTicketSendingOption }) => {
                const { viewModel, dynamicFormFields, form, person } = this.state().get(tariffUuid);

                return {
                  viewModel,
                  dynamicFormFields: this.#dynamicFormFactory.updateForm(
                    this.#dynamicFormFieldsFactory.updateFormOnSendToOwnerChange(
                      dynamicFormFields,
                      form.formGroup,
                      formFields,
                      ticketPersonId,
                      exhibitionSettings,
                      selectedTicketSendingOption
                    ),
                    formFields,
                    form.formGroup
                  ),
                  form,
                  person,
                  tariffUuid
                };
              })
            )
          ),
          reduceTicketHolderFormStatePipe()
        )
      ),
      map((updatedFormStates) => new Map([...this.state(), ...updatedFormStates]))
    );
  }

  private updateFormStateOnBuyerPersonChange$() {
    return combineLatest({
      resetFormState: this.#resetFormState$,
      person: this.#store.select(selectBuyerPerson)
    }).pipe(
      filter(({ resetFormState, person }) => this.#isFormStateCreated() && !resetFormState && !isNil(person)),
      switchMap(({ person }) =>
        this.#store.pipe(
          select(selectTicketHolderFormsTariffUuidsByPersonUuid(person.uuid)),
          first(),
          map((ticketHolderTariffUuids) =>
            reduce(
              ticketHolderTariffUuids,
              (updatedFormStates, tariffUuid) => {
                const { viewModel, dynamicFormFields, form } = this.state().get(tariffUuid);

                updatedFormStates.set(tariffUuid, {
                  viewModel,
                  dynamicFormFields,
                  form,
                  person: { ...person, type: PersonType.BuyerIsHolder }
                });

                return updatedFormStates;
              },
              new Map()
            )
          )
        )
      ),
      map((updatedFormStates) => new Map([...this.state(), ...updatedFormStates]))
    );
  }

  private updateFormStateOnGDPRChange$(): Observable<TicketHolderFormStates> {
    return this.#updateFormStatePairwiseFilter$.pipe(
      switchMap(([previousTicketHolderFormChanges, currentTicketHolderFormChanges]) =>
        from(keys(currentTicketHolderFormChanges)).pipe(
          updateFormFieldsFilterPipe(
            previousTicketHolderFormChanges,
            currentTicketHolderFormChanges,
            ({ changedFormFields }) =>
              !isEmpty(changedFormFields) && !isEmpty(intersection(keys(changedFormFields), [FieldKey.GDPR]))
          ),
          concatMap(({ tariffUuid }) =>
            this.#store.pipe(
              select(selectTicketHolderFormFieldsByTariffUuid(tariffUuid)),
              first(),
              map((formFields) => {
                const { viewModel, dynamicFormFields, form, person } = this.state().get(tariffUuid);
                const { formGroup } = form;

                const updatedDynamicFormFields = this.#dynamicFormFactory.updateForm(
                  this.#dynamicFormFieldsFactory.updateFormOnGDPRConsentChange(dynamicFormFields, formFields),
                  formFields,
                  formGroup
                );

                return {
                  viewModel: this.setViewModel(viewModel, formGroup),
                  dynamicFormFields: updatedDynamicFormFields,
                  form,
                  person,
                  tariffUuid
                };
              })
            )
          ),
          reduceTicketHolderFormStatePipe()
        )
      ),
      map((updatedFormStates) => new Map([...this.state(), ...updatedFormStates]))
    );
  }

  private setViewModel(viewModel: TicketHolderFormViewModel, formGroup: FormGroup): TicketHolderFormViewModel {
    const isGDPRConsentChecked = !!formGroup?.get(FieldKey.GDPR)?.get(OptionKey.GDPRConsent)?.value;
    const uploadedCVFiles: UploadedFiles = formGroup?.get(FieldKey.CV)?.value || [];
    const isGDPRConsentUncheckGuarded = isGDPRConsentChecked && !isEmpty(uploadedCVFiles);

    return {
      ...viewModel,
      checkboxUncheckGuards: {
        [FieldKey.GDPR]: {
          [OptionKey.GDPRConsent]: isGDPRConsentUncheckGuarded
        }
      }
    };
  }

  private updateFormStatePairwiseFilter$() {
    return this.#ticketHolderForms$.pipe(
      pairwise(),
      filter(([previousTicketHolderFormChanges, currentTicketHolderFormChanges]) => {
        const previousTicketHolderFormChangesKeys = keys(previousTicketHolderFormChanges);
        const currentTicketHolderFormChangesKeys = keys(currentTicketHolderFormChanges);

        return (
          !isEmpty(previousTicketHolderFormChangesKeys) &&
          !isEmpty(currentTicketHolderFormChangesKeys) &&
          isEqual(previousTicketHolderFormChangesKeys, currentTicketHolderFormChangesKeys)
        );
      })
    );
  }

  private isFormsStateSetPipe<T = TicketHolderForms>(): OperatorFunction<T, T> {
    return pipe(filter(() => !isNil(this.state()) && !!this.state().size));
  }

  private resetFormsState$(): Observable<TicketHolderFormStates> {
    return this.#resetFormState$.pipe(
      withLatestFrom(this.#store.select(selectIsLogoutProcessing)),
      filter(([resetFormState, isLogoutProcessing]) => resetFormState && !isLogoutProcessing),
      map(() => this.#initialState),
      tap(() => {
        this.#isFormStateCreated.set(false);

        this.#resetFormState$.next(false);
      })
    );
  }
}
